// Componente React per scegliere Aliquota IVA (>0%) oppure Nature IVA (0%)
// - Nel value della <option>: numero (es. 22) per aliquote > 0, codice (es. "N4") per nature
// - Nel testo visualizzato: "XX%" per aliquote; "0% <codice> <descrizione>" per nature
//
// Uso base:
// <AliquotaIvaSelect value={valoreSelezionato} onChange={(v) => setValoreSelezionato(v)} />
// dove `v` è un Number (per aliquote) oppure una String (per nature, es. "N4").

import React from "react";

// Aliquote IVA correnti più comuni in Italia (>0%)
const VAT_RATES = [4, 5, 10, 22];

// Nature IVA (0%) secondo specifiche FatturaPA (elenco essenziale e ordinato)
const VAT_NATURES = [
  { code: "N1", desc: "escluse ex art.15 del DPR 633/72" },
  { code: "N2.1", desc: "non soggette ad IVA ai sensi degli artt. Da 7 a 7- septies del DPR 633/72" },
  { code: "N2.2", desc: "non soggette - altri casi" },
  { code: "N3.1", desc: "non imponibili - esportazioni" },
  { code: "N3.2", desc: "non imponibili - cessioni intracomunitarie" },
  { code: "N3.3", desc: "non imponibili - cessioni verso San Marino" },
  { code: "N3.4", desc: "non imponibili - operazioni assimilate alle cessioni all’esportazione" },
  { code: "N3.5", desc: "non imponibili - a seguito di dichiarazioni d’intento" },
  { code: "N3.6", desc: "non imponibili - altre operazioni che non concorrono alla formazione del plafond" },
  { code: "N4", desc: "esenti" },
  { code: "N5", desc: "regime del margine / IVA non esposta in fattura" },
  { code: "N6.1", desc: "inversione contabile - cessione di rottami e altri materiali di recupero" },
  { code: "N6.2", desc: "inversione contabile - cessione di oro e argento ai sensi della legge 7/2000 nonché di oreficeria usata ad OPO" },
  { code: "N6.3", desc: "inversione contabile - subappalto nel settore edile" },
  { code: "N6.4", desc: "inversione contabile - cessione di fabbricati" },
  { code: "N6.5", desc: "inversione contabile - cessione di telefoni cellulari" },
  { code: "N6.6", desc: "inversione contabile - cessione di prodotti elettronici" },
  { code: "N6.7", desc: "inversione contabile - prestazioni comparto edile e settori connessi" },
  { code: "N6.8", desc: "inversione contabile - operazioni settore energetico" },
  { code: "N6.9", desc: "inversione contabile - altri casi" },
  { code: "N7", desc: "IVA assolta in altro stato UE (prestazione di servizi di telecomunicazioni, tele-radiodiffusione ed elettronici ex art. 7-sexies lett. f, g, art. 74- sexies DPR 633/72)" },
];

/**
 * AliquotaIvaSelect
 * @param {Object} props
 * @param {number|string|null} [props.value] - Valore selezionato: numero per aliquote (>0), string per nature (es. "N4").
 * @param {(e:React.ChangeEvent<HTMLSelectElement>)=>void} [props.onChange] - (Opzionale) Handler stile native event: riceve l'evento.
 * @param {(value:number|string)=>void} [props.onValueChange] - (Consigliato) Handler che riceve già il valore convertito.
 * @param {boolean} [props.includePlaceholder=true] - Mostra un'opzione placeholder iniziale.
 * @param {string} [props.placeholder="Seleziona aliquota o natura"] - Testo del placeholder.
 * @param {string} [props.id]
 * @param {string} [props.name]
 * @param {string} [props.className]
 * @param {boolean} [props.disabled]
 */
export default function AliquotaIvaSelect({
  value = null,
  onChange, // opzionale: evento
  onValueChange, // consigliato: valore già pronto
  includePlaceholder = true,
  placeholder = "Seleziona aliquota o natura",
  id,
  name,
  className,
  disabled,
}) {
  const handleChange = (e) => {
    const raw = e.target.value;
    const numeric = raw !== "" && !isNaN(Number(raw));
    const parsed = numeric ? Number(raw) : raw;
    // Prima: callback stile native (evento)
    onChange?.(e);
    // Poi: callback con valore già convertito
    onValueChange?.(parsed);
  };

  // Per far funzionare il controllo, la select deve ricevere sempre una stringa o un numero
  // Se value è null/undefined, usiamo "" per il placeholder
  const controlledValue = value ?? "";

  return (
    <select
      id={id}
      name={name}
      className={className}
      onChange={handleChange}
      value={controlledValue}
      disabled={disabled}
    >
      {includePlaceholder && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}

      {/* Aliquote IVA (>0%) */}
      <optgroup label="Aliquote IVA">
        {VAT_RATES.map((rate) => (
          <option key={`rate-${rate}`} value={rate}>
            {`${rate}%`}
          </option>
        ))}
      </optgroup>

      {/* Nature IVA (0%) */}
      <optgroup label="Nature IVA (0%)">
        {VAT_NATURES.map((n) => (
          <option key={n.code} value={n.code}>
            {`0% ${n.code} ${n.desc}`}
          </option>
        ))}
      </optgroup>
    </select>
  );
}

// Helper opzionali per chi usa il componente
export function isAliquota(value) {
  return typeof value === "number" && value > 0;
}

export function isNatura(value) {
  return typeof value === "string" && value.startsWith("N");
}

export function formatAliquotaONatura(value) {
  if (isAliquota(value)) return `${value}%`;
  const natura = VAT_NATURES.find((n) => n.code === value);
  if (natura) return `0% ${natura.code} ${natura.desc}`;
  return "";
}
